'use client';

import { Ref, useEffect, useImperativeHandle, useState } from 'react';
import { VariantProps } from 'tailwind-variants';
import { Drawer } from 'vaul';

import CloseIcon from '@/fo/icons/base/icons/close.svg';
import { UiKitButtonBase, UiKitButtonBaseProps } from '@/fo/ui/kit/button/base';
import { useMediaQuery } from '@/fo/ui/mod/media/query';

import { DialogContext } from './DialogContext';
import { styles } from './UiKitDrawerBase.styles';

type Variants = VariantProps<typeof styles>;

export interface UiKitDrawerBaseRef {
  close: () => void;
}

export interface UiKitDrawerBaseProps {
  children?: React.ReactNode;
  title?: string;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  direction?: Variants['direction'];
  position?: Variants['position'];
  className?: string;
  withoutMobile?: boolean;
  ref?: Ref<HTMLDivElement>;
  dismissible?: boolean;
  colorCross?: UiKitButtonBaseProps['color'];
  autoFocus?: boolean;
  drawerRef?: Ref<UiKitDrawerBaseRef>;
  contentClassName?: string;
  handleOnly?: boolean;
  closeButtonClassName?: string;
}

export function UiKitDrawerBase({
  title,
  children,
  trigger,
  onOpenChange,
  direction = 'left',
  position,
  className,
  contentClassName,
  withoutMobile = false,
  ref,
  dismissible = true,
  autoFocus = true,
  drawerRef,
  handleOnly = false,
  closeButtonClassName,
  ...props
}: UiKitDrawerBaseProps) {
  const slots = styles({ direction, withoutMobile, position });

  const isDesktop = useMediaQuery('sm');

  const [contentRef, setContentRef] = useState<HTMLDivElement | null>(null);

  const [open, setOpen] = useState(props.open);

  const close = () => {
    setOpen(false);
  };

  useImperativeHandle(drawerRef, () => ({ close }));

  useEffect(() => {
    setOpen(props.open);
  }, [props.open]);

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    onOpenChange?.(open);
  };

  const handleRef = (node: HTMLDivElement) => {
    setContentRef(node);

    if (ref) {
      if (typeof ref === 'function') {
        ref(node);
        return;
      }

      ref.current = node;
    }
  };

  return (
    <Drawer.Root
      open={open}
      onOpenChange={handleOpenChange}
      direction={!isDesktop && !withoutMobile ? 'bottom' : direction}
      dismissible={dismissible}
      handleOnly={handleOnly}
      repositionInputs={false}
    >
      {trigger && <Drawer.Trigger asChild>{trigger}</Drawer.Trigger>}
      <DialogContext.Provider value={{ contentRef }}>
        <Drawer.Portal>
          <Drawer.Overlay className={slots.overlay()} />
          <Drawer.Content
            autoFocus={autoFocus}
            ref={handleRef}
            className={slots.contentContainer({ className })}
          >
            {/* https://github.com/radix-ui/primitives/issues/2986 */}
            {title && (
              <Drawer.Title asChild>
                <span className="hidden">{title}</span>
              </Drawer.Title>
            )}
            <div className={slots.content({ className: contentClassName })}>
              <div
                aria-hidden
                className={slots.divider()}
              />

              {children}
              <Drawer.Close asChild>
                <UiKitButtonBase
                  rounded="full"
                  color="ghost"
                  noBorder={true}
                  className={slots.closeButton({
                    className: closeButtonClassName
                  })}
                >
                  <CloseIcon className={slots.closeIcon()} />
                </UiKitButtonBase>
              </Drawer.Close>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </DialogContext.Provider>
    </Drawer.Root>
  );
}
